import { FBDocTypeEssentials, NoSqlDoc } from '@rabbit/firebase/doctype';
import {
  SearchOptions,
  SearchResults,
  SearchResultItem,
  TOP_LEVEL_FIELDS,
  SORTABLE_FIELDS,
  SortDirection,
  getDirectionMultiplier,
  normalizeNumericFilters,
} from './types';
import { GetAlgoliaSearchIndex } from './client';
import moment from 'moment';

/**
 * Internal use only
 * Generic search function.
 * @param type document type (e.g. FBD_Vendable, FBD_Retailer_Public or FBD_Category_Public)
 * @param query search query test e.g. 'umbrella'
 * @param options search options
 * @returns an array of search results
 * @example
 * SearchGeneric(FBD_Vendable, 'umbrella')
 */
export async function SearchGeneric<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>,
  query: string,
  options?: SearchOptions,
): Promise<SearchResults<TYPE>> {

  // Auto-clean restrictToAttribues to remove unordered() wrappers
  const cleanRestrictToAttribues = (options?.restrictToAttribues || []).map(attr =>
    attr.replace(/^unordered\(([^)]+)\)$/, '$1')
  );
  const algoliaParams = {
    page: options?.page || 0,
    hitsPerPage: options?.hitsPerPage || 10,
    facetFilters: options?.facetFilters
      ? options.facetFilters.map((filter) => {
          // Check if this is a top-level field (extracted by transform function)
          const fieldName = TOP_LEVEL_FIELDS.includes(filter.name as any) 
            ? filter.name 
            : `payload.${filter.name}`;
          
          // Handle array values for `OR` filtering
          if (Array.isArray(filter.value)) {
            // Group array values for OR behavior in Algolia
            return filter.value.map(val => `${fieldName}:${val}`);
          } else {
            return `${fieldName}:${filter.value}`;
          }
        })
      : [],
    restrictSearchableAttributes: cleanRestrictToAttribues,
    numericFilters: options?.numericFilters ? normalizeNumericFilters(options.numericFilters) : [],
    // Note: Algolia sorting requires replica indices - cannot use 'sort' parameter directly
    filters: options?.filters,
  };

  const { index, shouldReverse } = await GetAlgoliaSearchIndex(type, options?.sort);
  let clientResults;
  let needsClientSideSort = false;

  try {
    clientResults = await index.search<SearchResultItem<TYPE>>(query, algoliaParams);
  } catch (error) {
    // If search fails (e.g. index doesn't exist), fall back to default index with client-side sorting
    console.warn(`Search failed on ${index.indexName}, falling back to default index and client-side sorting:`, error);
    const { index: defaultIndex } = await GetAlgoliaSearchIndex(type); // No sort options = base index
    clientResults = await defaultIndex.search<SearchResultItem<TYPE>>(query, algoliaParams);
    needsClientSideSort = true;
  }

  const res: SearchResults<TYPE> = {
    numitems: clientResults.nbHits,
    page: clientResults.page,
    numpages: clientResults.nbPages,
    items: [],
  };

  for (let i = 0; i < clientResults.hits.length; i++) {
    
    const hit = clientResults.hits[i];

  
    let payload: TYPE | any;
    
    // Extract payload (works for both legacy and new structures)
    if (hit.payload && typeof hit.payload === 'object' && Object.keys(hit.payload).length > 0) {
      payload = hit.payload as TYPE | any;
    } else {
      console.warn(`Skipping hit ${i}: missing or invalid payload`, hit.objectID);
      continue;
    }
    
    // If payload.docid is missing but objectID exists, use objectID as docid
    if (!payload.docid && hit.objectID) {
      (payload as any).docid = hit.objectID;
    }
    
    // Merge payload with top-level fields from transform function
    const hitAny = hit as any;
    const topLevelFields = TOP_LEVEL_FIELDS.reduce((acc, field) => {
      if (hitAny[field] !== undefined) acc[field] = hitAny[field];
      return acc;
    }, {} as Record<string, any>);
    
    const enhancedResult = { ...payload, ...topLevelFields } as TYPE;
    
    res.items.push(enhancedResult);
  }

  // Apply post-processing based on sorting strategy used
  applySortingStrategy(res, options?.sort, needsClientSideSort, shouldReverse);

  console.log(`SearchGeneric: got ${res.items.length} results for "${query}" on index ${index.indexName}`);
  return res;
}

/**
 * Applies the appropriate sorting strategy based on what happened during the search
 * @param results - The search results to sort
 * @param sortOptions - Original sort options from the request  
 * @param needsClientSideSort - Whether we fell back to default index
 * @param shouldReverse - Whether results should be reversed (from GetAlgoliaSearchIndex)
 */
function applySortingStrategy(
  results: any, 
  sortOptions?: string[], 
  needsClientSideSort = false,
  shouldReverse = false
): void {

  if (!sortOptions || sortOptions.length === 0) return;

  const primarySort = sortOptions[0];
  const [field, direction = 'desc'] = primarySort.split(':') as [string, SortDirection];
  
  if (needsClientSideSort) {
    // Applying client-side sort
    clientSideSort(results.items, field, direction);
  } else if (shouldReverse) {
    // Reversing results from replica/base index
    results.items.reverse();
  }
}

/**
 * Sorts array of items by the given field and direction
 * @param items - Array to sort in-place
 * @param field - Field name to sort by
 * @param direction - The sort direction ('asc' or 'desc')
 */
function clientSideSort(items: any[], field: string, direction: SortDirection): void {
  const multiplier = getDirectionMultiplier(direction);
  
  items.sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];
    
    // Handle null/undefined values - always sort to end regardless of direction
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;  // Always put nulls at the end
    if (bVal == null) return -1; // Always put nulls at the end
    
    // Convert to comparable values
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return -multiplier; // Fixed: was return multiplier
    if (aVal > bVal) return multiplier;  // Fixed: was return -multiplier
    return 0;
  });
}
